// SB6-ANA-FILT-05 — the shared filtered data-point set every Analysis
// widget reduces to (D1, AC13). Reads the live 7-axis selection from the
// store and fetches `GET …/{id}/data-points`. Returns the data points
// plus a Set of their vis-image ids so image-keyed widgets (Filmstrip,
// List "All images", Tiles "All images") can intersect their own image
// list against the filtered set without each issuing its own filtered
// query.
import { useMemo } from "react";
import type { DataPoint } from "@aeriosiq/types";
import { useDataPointFilterStore } from "./state/dataPointFilterStore.js";
import { useDataPointsQuery } from "./queries.js";
import { useWidgetSyncState } from "./filterRevision.js";

export interface UseFilteredDataPointsResult {
  dataPoints: DataPoint[];
  /** vis-image ids of the filtered data points (1:1 image↔DP). */
  visibleImageIds: Set<string>;
  /** data-point status keyed by vis-image id (for the INACTIVE chip). */
  statusByImageId: Map<string, DataPoint["status"]>;
  /** data-point id keyed by vis-image id (for status mutation). */
  dataPointIdByImageId: Map<string, string>;
  isPending: boolean;
  isError: boolean;
  total: number;
  /**
   * SB6-ANA-PROG-02 — true while the displayed data points do not answer
   * the latest filter revision (a refetch is in flight or queued). Drives
   * the per-widget sync indicator + dimming for the Filmstrip/List/Tiles/
   * Sketch widgets that reduce from this set.
   */
  outOfSync: boolean;
}

export function useFilteredDataPoints(
  dataAcquisitionId: string,
): UseFilteredDataPointsResult {
  const selection = useDataPointFilterStore((s) => s.selection);
  const query = useDataPointsQuery(dataAcquisitionId, selection);

  // ANA-R1-08 — the data-points query now uses `keepPreviousData`, so on a
  // filter change its data stays visible (as a placeholder from the previous
  // key) while the new selection's set is in flight. `isPlaceholderData`
  // catches that window precisely; `isFetching` covers an in-flight refetch
  // for the current key; the revision tag (`checkRevision`) covers the
  // queued-but-not-yet-fired window before `isFetching` flips. `checkRevision`
  // is safe here because this query's key encodes the FULL 7-axis selection +
  // AI sub-filter.
  const { outOfSync } = useWidgetSyncState([
    {
      data: query.data,
      isFetching: query.isFetching,
      isPlaceholderData: query.isPlaceholderData,
      checkRevision: true,
    },
  ]);

  return useMemo(() => {
    const dataPoints = query.data?.items ?? [];
    const visibleImageIds = new Set<string>();
    const statusByImageId = new Map<string, DataPoint["status"]>();
    const dataPointIdByImageId = new Map<string, string>();
    for (const dp of dataPoints) {
      if (dp.visImageId) {
        visibleImageIds.add(dp.visImageId);
        statusByImageId.set(dp.visImageId, dp.status);
        dataPointIdByImageId.set(dp.visImageId, dp.dataPointId);
      }
    }
    return {
      dataPoints,
      visibleImageIds,
      statusByImageId,
      dataPointIdByImageId,
      isPending: query.isPending,
      isError: query.isError,
      total: query.data?.total ?? 0,
      outOfSync,
    };
  }, [query.data, query.isPending, query.isError, outOfSync]);
}
